import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import { NavLink } from 'react-router-dom';
import { DataTable, EmptyState, Notice, PageHeader, useNotify, type DataTableColumn } from '@routes-plan/ui';

import { Client } from '../../../models/client';
import { Route } from '../../../models/route';
import { Office } from '../../../models/office';
import { useSelection } from '../../../components/context/SelectionContext';
import HeaderResponsive from '../../../components/layouts/HeaderResponsive';
import moment from 'moment-timezone';
import { LoanType } from '../../../models/loan-type';
import { formatCurrency } from '../../../utils/format/currency';
import PermissionButton from '../../../components/common/PermissionButton';
import { useResourcePermissions } from '../../../hooks/useResourcePermissions';
import StandardModal from '../../../components/modales/StandardModal';
import { useT, translate, currentLang } from '../../../i18n';
import { setPageTitle } from '../../../utils/pageTitle';
import { OfficeRouteFields, Section, SelectField } from '../components/ProcessFilters';
import { ScreenTourButton } from '../../../components/help/ScreenHelp';

interface ShowAlerts {
  showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
  showLoading: () => void;
  hideLoading: () => void;
}

const normalizeKey = (str: string) =>
  str.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim();

const excelDateToJS = (excelDate: number) =>
  moment(new Date(Math.round((excelDate - 25569 + 1) * 86400 * 1000))).format('YYYY-MM-DD');

const parseNumericValue = (value: unknown) => {
  if (value === undefined || value === null || value === '') return 0;
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;

  const normalizedValue = String(value)
    .trim()
    .replace(/\s+/g, '')
    .replace(/[^\d,.-]/g, '')
    .replace(/\.(?=\d{3}(?:\D|$))/g, '')
    .replace(',', '.');
  const parsedValue = Number(normalizedValue);

  return Number.isFinite(parsedValue) ? parsedValue : 0;
};

const hasMeaningfulValue = (value: unknown) => {
  if (value === undefined || value === null) return false;
  return String(value).trim() !== '';
};

const getPeriodCount = (termInDays: number, loanTypeKindId?: number | null) => {
  if (!Number.isFinite(termInDays) || termInDays <= 0) return 1;
  if (loanTypeKindId === 2) return Math.max(1, Math.round((termInDays / 30) * 4));
  if (loanTypeKindId === 3) return Math.max(1, Math.round(termInDays / 15));
  if (loanTypeKindId === 4) return Math.max(1, Math.round(termInDays / 30));
  return termInDays;
};

const getImportErrorMessage = (error: any) => {
  const responseData = error?.response?.data;

  if (typeof responseData?.message === 'string' && responseData.message.trim() !== '') {
    return responseData.message;
  }

  if (Array.isArray(responseData) && responseData.length > 0) {
    const firstDetail = responseData[0];
    if (typeof firstDetail?.message === 'string' && firstDetail.message.trim() !== '') {
      return firstDetail.message;
    }
  }

  if (typeof error?.message === 'string' && error.message.trim() !== '') {
    return error.message;
  }

  return translate(currentLang(), 'loanImports.saveError');
};

const getImportErrors = (error: any) => {
  const responseErrors = error?.response?.data?.errors;
  return Array.isArray(responseErrors)
    ? responseErrors.filter((item: unknown) => typeof item === 'string' && item.trim() !== '')
    : [];
};

const getImportErrorsFromResponse = (data: any, includeMessageFallback = false) => {
  const responseErrors = Array.isArray(data?.errors)
    ? data.errors.filter((item: unknown) => typeof item === 'string' && item.trim() !== '')
    : [];

  if (responseErrors.length > 0) {
    return responseErrors;
  }

  const responseDetails = Array.isArray(data?.details)
    ? data.details.filter((item: unknown) => typeof item === 'string' && item.trim() !== '')
    : [];

  if (responseDetails.length > 0) {
    return responseDetails;
  }

  if (includeMessageFallback && typeof data?.message === 'string' && data.message.trim() !== '') {
    return [data.message];
  }

  return [];
};

const getFirstValue = (row: Record<string, unknown>, aliases: string[]) => {
  for (const alias of aliases) {
    const value = row[alias];
    if (hasMeaningfulValue(value)) {
      return value;
    }
  }
  return '';
};

const isValidImportedDate = (value: unknown) => {
  if (!hasMeaningfulValue(value)) return false;
  return moment(String(value), 'YYYY-MM-DD', true).isValid();
};

const buildImportedRows = (
  rows: any[],
  interestRate: string,
  daysTerm: string,
  loanTypeKindId?: number | string,
  loanTypeId?: number | string
) => {
  const term = parseNumericValue(daysTerm);
  const numericLoanTypeKindId = Number(loanTypeKindId || 0);
  const periodCount = getPeriodCount(term, numericLoanTypeKindId);
  const isWeekly = numericLoanTypeKindId === 2;

  return rows.map((row: any, idx: number) => {
    const idNumber = getFirstValue(row, ['cedula', 'cédula', 'documento', 'identificacion', 'identificación', 'idnumber']);
    const fullName = getFirstValue(row, ['nombre', 'nombres', 'nombre cliente', 'cliente', 'fullname']);
    const mobilePhone = getFirstValue(row, ['telefono', 'teléfono', 'celular', 'movil', 'móvil', 'cellphone']);
    const mainAddress = getFirstValue(row, ['direccion', 'dirección', 'direccion domicilio', 'direccion cliente', 'mainaddress']);
    const loanDate = getFirstValue(row, ['fecha', 'fecha prestamo', 'fecha préstamo', 'fecha credito', 'fecha crédito', 'date']);
    const normalizedLoanDate = typeof loanDate === 'number' ? loanDate : String(loanDate);
    const interestFromLoanType = interestRate !== '' ? parseNumericValue(interestRate) : null;
    const interestFromFile = parseNumericValue(
      row['utilidad'] ??
      row['% utilidad'] ??
      row['porcentaje utilidad'] ??
      row['porcentaje de utilidad']
    );
    const utilidad = interestFromLoanType ?? interestFromFile;
    const total = parseNumericValue(row['valor prestamo']);
    const balance = parseNumericValue(row['saldo']);
    const paidTotal = Math.max(total - balance, 0);
    const installmentAmount = periodCount > 0
      ? Number((total / periodCount).toFixed(2))
      : 0;
    const capital = utilidad >= 0 ? total / (1 + utilidad / 100) : total;
    const utilidadValor = Math.round(total - capital);
    const paymentCountFromFile = parseNumericValue(
      row['count_pay'] ??
      row['no pagos'] ??
      row['no. pagos'] ??
      row['numero de pagos'] ??
      row['n pagos']
    );
    const paymentCount = paymentCountFromFile > 0
      ? paymentCountFromFile
      : installmentAmount > 0 && paidTotal > 0
        ? Math.ceil(paidTotal / installmentAmount)
        : 0;
    const fullPaymentCount = installmentAmount > 0
      ? Math.floor(paidTotal / installmentAmount)
      : 0;

    return {
      id: idx,
      rowNumber: idx + 1,
      position: parseNumericValue(row['posicion']),
      idNumber,
      fullName,
      mobilePhone,
      mainAddress,
      totalValue: total,
      installmentValue: installmentAmount,
      value: Math.round(capital),
      interestValue: utilidadValor,
      interestRate: utilidad,
      date: typeof normalizedLoanDate === 'number'
        ? excelDateToJS(normalizedLoanDate)
        : moment(normalizedLoanDate, ['YYYY-MM-DD', 'DD/MM/YYYY', 'DD/MM/YY', moment.ISO_8601], true).isValid()
          ? moment(normalizedLoanDate, ['YYYY-MM-DD', 'DD/MM/YYYY', 'DD/MM/YY', moment.ISO_8601], true).format('YYYY-MM-DD')
          : moment(normalizedLoanDate).format('YYYY-MM-DD'),
      balance,
      paidTotal,
      paymentCount,
      fullPaymentCount,
      loanTypeId: loanTypeId ?? null,
      isWeekly: isWeekly,
      visitDay: '',
    };
  });
};

const recalculateRowWithLoanType = (row: any, loanType: LoanType) => {
  const term = parseNumericValue(loanType.term);
  const utilidad = parseNumericValue(loanType.interestRate);
  const loanTypeKindId = Number(loanType.loanTypeKind.id);
  const weekly = loanTypeKindId === 2;
  const periodCount = getPeriodCount(term, loanTypeKindId);
  const total = parseNumericValue(row.totalValue);
  const balance = parseNumericValue(row.balance);
  const paidTotal = Math.max(total - balance, 0);
  const installmentAmount = periodCount > 0
    ? Number((total / periodCount).toFixed(2))
    : 0;
  const capital = utilidad >= 0 ? total / (1 + utilidad / 100) : total;
  const utilidadValor = Math.round(total - capital);
  const paymentCount = installmentAmount > 0 && paidTotal > 0
    ? Math.ceil(paidTotal / installmentAmount)
    : 0;
  const fullPaymentCount = installmentAmount > 0
    ? Math.floor(paidTotal / installmentAmount)
    : 0;

  return {
    ...row,
    loanTypeId: loanType.id,
    isWeekly: weekly,
    visitDay: weekly ? row.visitDay ?? '' : '',
    interestRate: utilidad,
    totalValue: total,
    installmentValue: installmentAmount,
    value: Math.round(capital),
    interestValue: utilidadValor,
    balance,
    paidTotal,
    paymentCount,
    fullPaymentCount,
  };
};

const validateImportedRows = (rows: any[]) => {
  const validationErrors: string[] = [];

  rows.forEach((row: any, idx: number) => {
    const rowNumber = Number(row?.rowNumber ?? idx + 1);
    const missingFields: string[] = [];

    if (!hasMeaningfulValue(row?.idNumber)) missingFields.push('cedula');
    if (!hasMeaningfulValue(row?.fullName)) missingFields.push('nombre');
    if (!hasMeaningfulValue(row?.mainAddress)) missingFields.push('direccion');
    if (!hasMeaningfulValue(row?.date)) missingFields.push('fecha');

    if (missingFields.length > 0) {
      // Los nombres de campo son las columnas del archivo: no se traducen
      validationErrors.push(
        translate(currentLang(), 'loanImports.rowMissingFields', { row: rowNumber, fields: missingFields.join(', ') })
      );
      return;
    }

    if (!isValidImportedDate(row?.date)) {
      validationErrors.push(
        translate(currentLang(), 'loanImports.rowInvalidDate', { row: rowNumber, date: String(row?.date) })
      );
    }
  });

  return validationErrors;
};


const LoanImport: React.FC<ShowAlerts> = ({ showLoading, hideLoading }) => {
  const t = useT();
  const notify = useNotify();
  const showAlert = (message: string, severity: 'success' | 'info' | 'warning' | 'error' = 'info') => notify[severity](message);
  useEffect(() => {
    setPageTitle('menu.processes.import');
  }, []);
  const [fileName, setFileName] = useState('');
  const { canCreate, canViewErrors } = useResourcePermissions('/processes/import');
  // El historial es otra pantalla y el API pide leer /processes/imports: el botón usa ese permiso.
  const { canRead: canReadHistory } = useResourcePermissions('/processes/imports');

  const [offices, setOffices] = useState<Office[]>([]);
  const [officeRoutes, setOfficeRoutes] = useState<Route[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [fileRows, setFileRows] = useState<any[]>([]);
  const [rawFileRows, setRawFileRows] = useState<any[]>([]);
  const [loanTypes, setLoanTypes] = useState<LoanType[]>([]);
  const [importErrors, setImportErrors] = useState<string[]>([]);
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [termDays, setTermDays] = useState("");
  const [interestRate, setInterestRate] = useState("");
  const [selectedLoanTypeIsWeekly, setSelectedLoanTypeIsWeekly] = useState(false);
  const [selectedLoanTypeKindId, setSelectedLoanTypeKindId] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  // Si no reseteamos el input file, algunos navegadores no disparan `onChange`
  // al intentar seleccionar otro archivo (o incluso el mismo).
  const [fileInputKey, setFileInputKey] = useState(0);
  const {
    office_id: officeId,
    setOffice_id: setOfficeId,
    collect_id: routeId,
    setCollect_id: setRouteId,
  } = useSelection();
  const [loanTypeId, setLoanTypeId] = useState('');
  const selectedPaymentCount = getPeriodCount(
    parseNumericValue(termDays),
    selectedLoanTypeKindId ?? undefined,
  );
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setFileRows([]);
    setRawFileRows([]);
    setImportErrors([]);
    setErrorModalOpen(false);
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);

    const reader = new FileReader();
    try {

      showLoading();
      reader.onload = (evt) => {
        const data = new Uint8Array(evt.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const rawJson = XLSX.utils.sheet_to_json(sheet);

        // Renombrar claves normalizadas
        const renamed = rawJson.map((row: any) => {
          const rowNorm: any = {};
          for (const key in row) {
            const newKey = normalizeKey(key); // ejemplo: "valor prestamo"
            rowNorm[newKey] = row[key];
          }
          return rowNorm;
        }).filter((row: Record<string, unknown>) => {
          const requiredCandidates = [
            getFirstValue(row, ['cedula', 'cédula', 'documento', 'identificacion', 'identificación', 'idnumber']),
            getFirstValue(row, ['nombre', 'nombres', 'nombre cliente', 'cliente', 'fullname']),
            getFirstValue(row, ['direccion', 'dirección', 'direccion domicilio', 'direccion cliente', 'mainaddress']),
            getFirstValue(row, ['fecha', 'fecha prestamo', 'fecha préstamo', 'fecha credito', 'fecha crédito', 'date']),
            row['valor prestamo'],
            row['saldo']
          ];

          return requiredCandidates.some(hasMeaningfulValue);
        });

        setRawFileRows(renamed);
      };
      reader.readAsArrayBuffer(file);
    } catch (error) {
      console.error('Error reading file:', error);
      showAlert(t('loanImports.readFileError'), 'error');
    } finally {
      hideLoading();
    }
  };


  const handleSubmitData = async () => {
    if (!canCreate) {
      showAlert(t('loanImports.noPermissionSaveImports'), 'warning');
      return;
    }
    if (!routeId) {
      showAlert(t('loanImports.selectRouteFirst'), 'warning');
      return;
    }
    if (!loanTypeId) {
      showAlert(t('loanImports.selectLoanTypeFirst'), 'warning');
      return;
    }
    const weeklyRowsWithoutVisitDay = fileRows.filter(
      (row) => row.isWeekly && (row.visitDay === '' || row.visitDay === undefined || row.visitDay === null)
    );
    if (weeklyRowsWithoutVisitDay.length > 0) {
      showAlert(
        t('loanImports.weeklyRouteDayMissing', {
          rows: weeklyRowsWithoutVisitDay.map((row) => row.rowNumber).join(', '),
        }),
        'warning'
      );
      return;
    }
    if (importErrors.length > 0) {
      setErrorModalOpen(canViewErrors);
      showAlert(t('loanImports.fileHasErrors'), 'warning');
      return;
    }
    if (fileRows.length === 0) {
      showAlert(t('loanImports.noValidRows'), 'warning');
      return;
    }
    try {
      showLoading();
      const payload = {
        rows: fileRows,
        routeId,
        loanTypeId,  // utilidad del 20%
      };
      const response = await axios.post('/imports/loans', payload);
      const data = response?.data ?? {};
      const responseErrors = getImportErrorsFromResponse(data);
      const importedRows = Array.isArray(data.resultados) ? data.resultados : [];
      const failedPostImportProcesses = Array.isArray(data.postImportProcesses)
        ? data.postImportProcesses.filter((process: any) => process.status === 'error')
        : [];

      if (responseErrors.length > 0) {
        setImportErrors(responseErrors);
        setErrorModalOpen(canViewErrors);
        showAlert(
          `${data.message || t('loanImports.validationErrorsDefault')} ${t('loanImports.seeErrorsHint')}`,
          'warning'
        );
        return;
      }

      if (importedRows.length === 0) {
        setImportErrors(responseErrors);
        setErrorModalOpen(canViewErrors && responseErrors.length > 0);
        showAlert(
          data.message || t('loanImports.nothingSaved'),
          responseErrors.length > 0 ? 'warning' : 'warning'
        );
        return;
      }

      if (failedPostImportProcesses.length > 0) {
        const processDetails = failedPostImportProcesses
          .map((process: any) => `${process.processName}: ${process.errorMessage || t('loanImports.noDetail')}`)
          .join(' | ');

        console.warn('Procesos posteriores con error:', failedPostImportProcesses);
        showAlert(
          `${data.message || t('loanImports.completedWithWarnings')} ${t('loanImports.warningDetail', { details: processDetails })}`,
          'warning'
        );
      } else {
        showAlert(data.message || t('loanImports.importOk'), 'success');
      }
      setImportErrors([]);
      setErrorModalOpen(false);
      setFileRows([]);
      setRawFileRows([]);
      // Resetear el input para permitir re-cargar otro archivo sin refrescar página.
      if (fileInputRef.current) fileInputRef.current.value = '';
      setFileInputKey(k => k + 1);
    } catch (error: any) {
      console.error('Error detallado:', error.response?.data || error.message || error);
      const errors = getImportErrorsFromResponse(error?.response?.data, true) || getImportErrors(error);
      const errorMessage = getImportErrorMessage(error);
      setImportErrors(errors);
      setErrorModalOpen(canViewErrors && errors.length > 0);
      showAlert(
        errors.length > 0
          ? `${errorMessage} ${t('loanImports.seeErrorsHint')}`
          : errorMessage,
        errors.length > 0 ? 'warning' : 'error'
      );
    } finally {
      hideLoading();
    }
  };

  const handleChangeRowLoanType = (rowId: number, newLoanTypeId: number) => {
    const selectedLoanType = loanTypes.find((c) => c.id === Number(newLoanTypeId));
    if (!selectedLoanType) return;

    setFileRows((prev) =>
      prev.map((row) =>
        row.id === rowId ? recalculateRowWithLoanType(row, selectedLoanType) : row
      )
    );
  };

  const handleChangeRowVisitDay = (rowId: number, visitDay: string) => {
    setFileRows((prev) =>
      prev.map((row) =>
        row.id === rowId ? { ...row, visitDay: visitDay } : row
      )
    );
  };

  const money = (v: unknown) => formatCurrency(Number(v ?? 0));
  const dayOptions = ['1', '2', '3', '4', '5', '6', '0'].map((d) => ({ id: d, name: t(`loanImports.day${d}` as 'loanImports.day1') }));
  const columns: DataTableColumn<any>[] = [
    { id: 'rowNumber', header: t('loanImports.colRow'), value: (r) => r.rowNumber, align: 'right', width: 64, sortable: true },
    { id: 'fullName', header: t('common.name'), value: (r) => String(r.fullName ?? ''), sortable: true },
    { id: 'idNumber', header: t('common.idNumber'), value: (r) => String(r.idNumber ?? ''), sortable: true },
    { id: 'position', header: t('loanImports.colPosition'), value: (r) => r.position, align: 'right', hideBelow: 'md' },
    { id: 'mobilePhone', header: t('common.phone'), value: (r) => String(r.mobilePhone ?? ''), hideBelow: 'md' },
    { id: 'mainAddress', header: t('common.address'), value: (r) => String(r.mainAddress ?? ''), wrap: true, hideBelow: 'md' },
    { id: 'date', header: t('common.date'), value: (r) => r.date, sortable: true },
    { id: 'totalValue', header: t('loanImports.colTotalValue'), value: (r) => Number(r.totalValue ?? 0), render: (r) => money(r.totalValue), align: 'right', sortable: true },
    { id: 'installmentValue', header: t('loanImports.colInstallment'), value: (r) => Number(r.installmentValue ?? 0), render: (r) => money(r.installmentValue), align: 'right' },
    { id: 'value', header: t('loanImports.colLoan'), value: (r) => Number(r.value ?? 0), render: (r) => money(r.value), align: 'right', hideBelow: 'sm' },
    {
      id: 'loanTypeId',
      header: t('loanImports.colLoanType'),
      value: (r) => loanTypes.find((lt) => lt.id === Number(r.loanTypeId ?? loanTypeId))?.name ?? '',
      render: (r) => (
        <TextField
          select
          size="small"
          value={r.loanTypeId ?? loanTypeId ?? ''}
          onChange={(e) => handleChangeRowLoanType(Number(r.id), Number(e.target.value))}
          inputProps={{ 'aria-label': `${t('loanImports.colLoanType')} ${t('loanImports.colRow')} ${r.rowNumber}` }}
          sx={{ minWidth: 170 }}
        >
          {loanTypes.map((loanType: LoanType) => (
            <MenuItem key={loanType.id} value={loanType.id}>
              {loanType.name}
            </MenuItem>
          ))}
        </TextField>
      ),
    },
    {
      id: 'visitDay',
      header: t('loanImports.colRouteDay'),
      value: (r) => (r.isWeekly ? dayOptions.find((d) => d.id === String(r.visitDay))?.name ?? '' : t('loanImports.notApplicable')),
      render: (r) =>
        r.isWeekly ? (
          <TextField
            select
            size="small"
            value={r.visitDay ?? ''}
            error={r.visitDay === '' || r.visitDay === undefined || r.visitDay === null}
            onChange={(e) => handleChangeRowVisitDay(Number(r.id), String(e.target.value))}
            SelectProps={{ displayEmpty: true }}
            inputProps={{ 'aria-label': `${t('loanImports.colRouteDay')} ${t('loanImports.colRow')} ${r.rowNumber}` }}
            sx={{ minWidth: 150 }}
          >
            <MenuItem value="">
              <em>{t('loanImports.selectDay')}</em>
            </MenuItem>
            {dayOptions.map((d) => (
              <MenuItem key={d.id} value={d.id}>
                {d.name}
              </MenuItem>
            ))}
          </TextField>
        ) : (
          <Typography variant="body2" color="text.secondary">
            {t('loanImports.notApplicable')}
          </Typography>
        ),
    },
    { id: 'interestRate', header: t('lend.utility'), value: (r) => Number(r.interestRate ?? 0), align: 'right', hideBelow: 'md' },
    { id: 'interestValue', header: t('loanImports.colUtilityValue'), value: (r) => Number(r.interestValue ?? 0), render: (r) => money(r.interestValue), align: 'right', hideBelow: 'md' },
    { id: 'balance', header: t('loanImports.colBalance'), value: (r) => Number(r.balance ?? 0), render: (r) => money(r.balance), align: 'right', sortable: true },
    { id: 'paidTotal', header: t('loanImports.colPaid'), value: (r) => Number(r.paidTotal ?? 0), render: (r) => money(r.paidTotal), align: 'right', hideBelow: 'md' },
    { id: 'paymentCount', header: t('loanImports.paymentCount'), value: (r) => Number(r.paymentCount ?? 0), align: 'right', hideBelow: 'md' },
  ];

  // cargar oficinas y cobros
  useEffect(() => {
    const fetchData = async () => {
      showLoading();
      try {
        const officesResponse = axios.get('offices');
        const officeRoutesResponse = officeId ? axios.get(`offices/${officeId}/routes`) : null;

        const [officesData, officeRoutesData] = await Promise.all([
          officesResponse,
          officeRoutesResponse,
        ]);

        if (officesData) setOffices(officesData.data.data);
        if (officeRoutesData) setOfficeRoutes(officeRoutesData.data);
        setClients([]);
      } catch (error) {
        showAlert(t('loanImports.loadOfficesRoutesError'), 'error');
      } finally {
        hideLoading();
      }
    };

    fetchData();
  }, [officeId]);
  useEffect(() => {
    if (officeId) {
      const fetchLoanTypes = async () => {
        showLoading();
        try {
          const { data } = await axios.get(`offices/${officeId}/loan-types/complete`);
          setLoanTypes(data.loanTypes)
        } catch (error) {
          showAlert(t('loanImports.noLoanTypes'), 'warning');
        } finally {
          hideLoading();
        }
      };

      fetchLoanTypes();
    }
  }, [officeId]);
  useEffect(() => {
    const fetchRouteClients = async () => {
      if (!routeId) {
        showAlert(t('loanImports.noRoutes'), 'info');
        return;
      }
      showLoading();
      try {
        const { data } = await axios.get(`routes/${routeId}/clients`);
        setClients(data.data);
        showAlert(t('loanImports.clientsLoaded'), 'info');
      } catch (error) {
        showAlert(t('loanImports.clientsLoadError'), 'error');
      } finally {
        hideLoading();
      }
    };

    fetchRouteClients();
  }, [routeId]);
  useEffect(() => {
    if (!loanTypes || loanTypes.length === 0) {
      setLoanTypeId('');
      setTermDays('');
      setInterestRate('');
      setSelectedLoanTypeIsWeekly(false);
      setSelectedLoanTypeKindId(null);
    }
  }, [loanTypes]);
  useEffect(() => {
    if (rawFileRows.length === 0) {
      setFileRows([]);
      setImportErrors([]);
      setErrorModalOpen(false);
      return;
    }

    const importedRows = buildImportedRows(
      rawFileRows,
      interestRate,
      termDays,
      selectedLoanTypeKindId ?? undefined,
      loanTypeId ? Number(loanTypeId) : undefined
    );
    const validationErrors = validateImportedRows(importedRows);

    setFileRows(importedRows);
    setImportErrors(validationErrors);
    setErrorModalOpen(canViewErrors && validationErrors.length > 0);
  }, [rawFileRows, termDays, interestRate, selectedLoanTypeKindId, loanTypeId, canViewErrors]);

  const onLoanTypeChange = (selectedLoanTypeId: string) => {
    setLoanTypeId(selectedLoanTypeId);
    const selectedLoanType = loanTypes.find((loanType: LoanType) => loanType.id === Number(selectedLoanTypeId));
    if (selectedLoanType) {
      setTermDays(selectedLoanType.term.toString());
      setInterestRate(selectedLoanType.interestRate.toString());
      setSelectedLoanTypeKindId(Number(selectedLoanType.loanTypeKind.id));
      setSelectedLoanTypeIsWeekly(selectedLoanType.loanTypeKind.id === 2);
    }
  };

  const saveButton = (tourId?: string) => (
    <PermissionButton
      allowed={canCreate}
      deniedTooltip={t('loanImports.noPermissionSave')}
      variant="contained"
      startIcon={<SaveOutlinedIcon />}
      onClick={handleSubmitData}
      disabled={fileRows.length === 0}
      data-tour={tourId}
    >
      {fileRows.length > 0 ? t('loanImports.saveCount', { count: fileRows.length }) : t('common.save')}
    </PermissionButton>
  );

  return (
    <HeaderResponsive>
      <PageHeader
        title={t('menu.processes.import')}
        description={t('loanImports.subtitle')}
        helpAction={<ScreenTourButton />}
        actions={
          <>
            <PermissionButton
              allowed={canReadHistory}
              deniedTooltip={t('loanImports.noPermissionHistory')}
              variant="outlined"
              startIcon={<HistoryOutlinedIcon />}
              component={NavLink}
              to="/processes/imports"
              data-tour="import-history"
            >
              {t('loanImports.history')}
            </PermissionButton>
            {saveButton('import-save')}
          </>
        }
      />

      <Box
        sx={{
          display: 'grid',
          gap: 2,
          mb: 2,
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'repeat(2, minmax(0, 1fr))' },
          alignItems: 'stretch',
        }}
      >
        <Section title={t('loanImports.stepDestination')}>
          <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))' } }}>
            <OfficeRouteFields
              idPrefix="import"
              tourPrefix="import"
              offices={offices}
              routes={officeRoutes}
              officeId={officeId}
              routeId={routeId}
              onOfficeChange={(v) => setOfficeId(v)}
              onRouteChange={(v) => setRouteId(v)}
            />
            <Box sx={{ gridColumn: '1 / -1' }}>
              <SelectField
                id="import-loan-type"
                tourId="import-loan-type"
                label={t('loanImports.selectLoanType')}
                value={loanTypeId}
                options={loanTypes}
                onChange={onLoanTypeChange}
                disabled={loanTypes.length === 0}
                required
              />
            </Box>
            {loanTypeId && (
              <>
                <TextField
                  id="import-payment-count"
                  data-tour="import-terms"
                  fullWidth
                  label={t('loanImports.paymentCount')}
                  value={selectedPaymentCount}
                  InputProps={{ readOnly: true }}
                />
                <TextField
                  id="import-utility"
                  fullWidth
                  label={t('lend.utility')}
                  value={interestRate ?? ''}
                  InputProps={{ readOnly: true }}
                />
              </>
            )}
          </Box>
        </Section>

        <Section title={t('loanImports.stepFile')} tourId="import-file">
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('loanImports.fileHint')}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5 }}>
            <Button variant="outlined" component="label" startIcon={<UploadFileOutlinedIcon />}>
              {t('loanImports.selectFile')}
              <input key={fileInputKey} ref={fileInputRef} type="file" hidden accept=".xlsx, .xls" onChange={handleFileUpload} />
            </Button>
            <Typography variant="body2" color={fileName ? 'text.primary' : 'text.secondary'} noWrap sx={{ minWidth: 0, flex: '1 1 160px' }} title={fileName}>
              {fileName || t('loanImports.noFile')}
            </Typography>
          </Box>
          {importErrors.length > 0 && (
            <Box sx={{ mt: 2 }} data-tour="import-errors">
              <Notice
                severity="warning"
                title={importErrors.length === 1 ? t('loanImports.errorsFoundOne') : t('loanImports.errorsFoundMany', { count: importErrors.length })}
                action={
                  <PermissionButton
                    allowed={canViewErrors}
                    deniedTooltip={t('loanImports.noPermissionErrors')}
                    color="inherit"
                    size="small"
                    startIcon={<ErrorOutlineOutlinedIcon />}
                    onClick={() => setErrorModalOpen(true)}
                  >
                    {t('loanImports.viewErrors', { count: importErrors.length })}
                  </PermissionButton>
                }
              >
                {t('loanImports.fileHasErrors')}
              </Notice>
            </Box>
          )}
        </Section>
      </Box>

      <Section title={t('loanImports.previewTitle')} action={fileRows.length > 0 ? saveButton() : undefined}>
        <DataTable<any>
          rows={fileRows}
          columns={columns}
          getRowId={(r) => r.id}
          searchable
          exportFileName={t('loanImports.exportFileName')}
          initialPageSize={50}
          ariaLabel={t('loanImports.previewTitle')}
          tourId="import-preview"
          emptyState={<EmptyState dense icon={<UploadFileOutlinedIcon />} title={t('loanImports.empty')} description={t('loanImports.emptyHint')} />}
        />
      </Section>

      <StandardModal
        open={errorModalOpen}
        onClose={() => setErrorModalOpen(false)}
        title={t('loanImports.errorsTitle')}
        maxWidth={900}
        actions={
          <>
            <span />
            <Button variant="contained" onClick={() => setErrorModalOpen(false)}>
              {t('common.close')}
            </Button>
          </>
        }
      >
        <Typography variant="body2" color="text.secondary" mb={2}>
          {importErrors.length === 1
            ? t('loanImports.errorsFoundOne')
            : t('loanImports.errorsFoundMany', { count: importErrors.length })}
        </Typography>

        <Box display="flex" flexDirection="column" gap={1.5}>
          {importErrors.map((error, index) => (
            <Box
              key={`${index}-${error}`}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                p: 1.5,
              }}
            >
              <Typography variant="subtitle2" color="error.main" mb={0.5}>
                {t('loanImports.errorNumber', { n: index + 1 })}
              </Typography>
              <Typography variant="body2">{error}</Typography>
            </Box>
          ))}
        </Box>
      </StandardModal>
    </HeaderResponsive>
  );
};

export default LoanImport;
